import { createContext, useCallback, useContext, useEffect, useRef, type DependencyList, type ReactNode } from 'react';

export interface BreakdownFlushRegistration {
  /** Persist any pending edits to the server. Safe to call when clean (no-op). */
  flush: () => Promise<void>;
}

export interface BreakdownFlushContextValue {
  flush: () => Promise<void>;
  registerFlush: (reg: BreakdownFlushRegistration) => symbol;
  unregisterFlush: (id: symbol) => void;
}

const BreakdownFlushContext = createContext<BreakdownFlushContextValue | null>(null);

export function BreakdownFlushProvider({ children }: { children: ReactNode }) {
  // Single-registrant model: only one breakdown surface is mounted at a time
  // (generic OR single-category, gated by job.projectType in JobDetail). The
  // id-based unregister guards against unmount/remount races where the new
  // mount registers before the old mount's cleanup runs.
  const regRef = useRef<{ id: symbol; reg: BreakdownFlushRegistration } | null>(null);

  const registerFlush = useCallback((reg: BreakdownFlushRegistration) => {
    const id = Symbol('breakdown-flush');
    regRef.current = { id, reg };
    return id;
  }, []);

  const unregisterFlush = useCallback((id: symbol) => {
    if (regRef.current?.id === id) {
      regRef.current = null;
    }
  }, []);

  const flush = useCallback(
    () => regRef.current?.reg.flush() ?? Promise.resolve(),
    [],
  );

  const value: BreakdownFlushContextValue = {
    flush,
    registerFlush,
    unregisterFlush,
  };

  return (
    <BreakdownFlushContext.Provider value={value}>
      {children}
    </BreakdownFlushContext.Provider>
  );
}

export function useBreakdownFlush(): BreakdownFlushContextValue {
  const ctx = useContext(BreakdownFlushContext);
  if (!ctx) {
    throw new Error('useBreakdownFlush must be used inside <BreakdownFlushProvider>');
  }
  return ctx;
}

export function useRegisterBreakdownFlush(
  build: () => BreakdownFlushRegistration,
  deps: DependencyList,
) {
  const { registerFlush, unregisterFlush } = useBreakdownFlush();
  useEffect(() => {
    const id = registerFlush(build());
    return () => unregisterFlush(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFlush, unregisterFlush, ...deps]);
}
